// src/components/Rdo.jsx
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import "./Rdo.css";
import { supabase } from "../supabaseClient";
import { FaTimes, FaTrash, FaExpand, FaCompress, FaChevronLeft, FaChevronRight, FaCalendarAlt } from "react-icons/fa";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudSun,
  faCloudShowersHeavy,
  faPersonDigging,
  faUserGroup,
  faGear,
  faTriangleExclamation,
  faCamera,
  faPrint,
  faTruck,
  faClockRotateLeft,
  faCloud,
  faBolt,
  faSnowflake,
  faSmog,
  faMoon,
  faCloudMoon,
} from "@fortawesome/free-solid-svg-icons";
import { faSun, faFilePdf } from "@fortawesome/free-regular-svg-icons";
import BuscaInsumo from "./BuscaInsumo";
import RdoPdf from './RdoPdf';
import RdoSkeleton from "./skeletons/RdoSkeletons";
import CalendarioDiarioObra from "./RdoCalendario";

import dataTransferImage from "../assets/data-transfer.png";

// ── Data de corte para o novo layout de clima ────────────────────────
const DATA_CORTE_NOVO_LAYOUT_CLIMA = "2026-09-24";

// ── Data de corte para atividades em tópicos ─────────────────────────
const DATA_CORTE_TOPICOS_ATIVIDADES = "2026-10-06";

const comprimirImagemParaBase64 = (file, maxLado = 800, qualidade = 0.6) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);

  img.onload = () => {
    let { width, height } = img;
    if (width > height && width > maxLado) {
      height = Math.round((height * maxLado) / width);
      width = maxLado;
    } else if (height > maxLado) {
      width = Math.round((width * maxLado) / height);
      height = maxLado;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);

    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL("image/jpeg", qualidade));
  };

  img.onerror = (e) => {
    URL.revokeObjectURL(url);
    reject(e);
  };

  img.src = url;
});

const base64ParaFile = (base64, nome) => {
  const [meta, dados] = base64.split(",");
  const mime = meta.match(/:(.*?);/)[1];
  const bin = atob(dados);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], nome, { type: mime });
};

// Remove acentos/caracteres especiais para uso seguro em keys do Storage
const slugify = (texto) =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")   // remove acentos
    .replace(/[ºª°]/g, "")             // remove indicadores ordinais
    .replace(/[^a-zA-Z0-9._-]+/g, "-") // tudo que não é seguro vira "-"
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

// ── Helpers para tópicos de atividades ──────────────────────────────
const TOPICO_VAZIO = () => ({ texto: "", empresa: "" });

const montarTopicos = (descricao, topicosSalvos) => {
  if (Array.isArray(topicosSalvos) && topicosSalvos.length) return topicosSalvos;
  const linhas = (descricao || "").split("\n").filter((l) => l.trim());
  return linhas.length ? linhas.map((texto) => ({ texto, empresa: "" })) : [TOPICO_VAZIO()];
};

const juntarTopicos = (topicos) =>
  topicos.map((t) => t.texto.trim()).filter(Boolean).join("\n");

// Campo que cresce e quebra linha conforme o texto
const TopicoTextarea = ({ value, onChange, onKeyDown, placeholder, inputRef }) => {
  const localRef = useRef(null);

  useLayoutEffect(() => {
    const el = localRef.current;
    if (!el) return;
    el.style.setProperty("height", "auto", "important");
    el.style.setProperty("height", `${el.scrollHeight}px`, "important");
  }, [value]);

  return (
    <textarea
      ref={(el) => { localRef.current = el; if (inputRef) inputRef(el); }}
      rows={1}
      className="rdo-topico-input"
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
    />
  );
};

const Rdo = ({
  notaId,
  onClose,
  usuarioId,
  projetoAtual,
  rdosDaPilha = [],
  indexAtual = -1,
  onNavegarRdo = null,
  pilhaId = null,
}) => {
  const responsavelInputRef = useRef(null);
  const [data, setData] = useState({
    inicio_obra: "",
    termino_obra: "",
    atraso_dias: "",
    feriado: false,
    dia_compensado: false,
    clima_manha: "",
    clima_tarde: "",
    obra_op_manha: "",
    obra_op_tarde: "",
    efetivo_proprio: [{ funcao: "", total: "", presentes: "" }],
    efetivo_terceirizado: [{ funcao: "", total: "", presentes: "" }],
    recebimento_materiais: [{ codigo: "", descricao: "", unidade: "", quantidade: "", empresa: "" }],
    equipamentos: [{ codigo: "", descricao: "", total: "", em_uso: "" }],
    intercorrencias: "",
    tem_intercorrencias: false,
    responsavel_preenchimento: "",
    fotos: [],
  });
  const [dataOriginal, setDataOriginal] = useState("");
  const [diaSemanaOriginal, setDiaSemanaOriginal] = useState("");
  const [pavimentosAtividades, setPavimentosAtividades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInicial, setLoadingInicial] = useState(true);
  const [projetoNome, setProjetoNome] = useState("");
  const [projetoImagem, setProjetoImagem] = useState("");
  const [engenheiroNome, setEngenheiroNome] = useState("");
  const [rdoId, setRdoId] = useState(null);
  const [rdoCarregado, setRdoCarregado] = useState(false);
  const [sugestoesMembros, setSugestoesMembros] = useState([]);
  const [mostrarSugestoesMembros, setMostrarSugestoesMembros] = useState(false);
  const [buscaInsumoAberta, setBuscaInsumoAberta] = useState(false);
  const [linhaBuscaAtiva, setLinhaBuscaAtiva] = useState(null);
  const [tipoInsumoAtivo, setTipoInsumoAtivo] = useState(null);
  const [fotoEmExibicao, setFotoEmExibicao] = useState(null);
  const [telaCheia, setTelaCheia] = useState(false);
  const [pavimentoFotoAlvo, setPavimentoFotoAlvo] = useState(null);
  const [fotoPavimentoEmExibicao, setFotoPavimentoEmExibicao] = useState(null);
  const [legendaEditando, setLegendaEditando] = useState(null);
  const [pavimentoEditando, setPavimentoEditando] = useState(null);
  const [historicoPavimento, setHistoricoPavimento] = useState(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [buscaHistorico, setBuscaHistorico] = useState("");
  const [rdoCreatedAt, setRdoCreatedAt] = useState(null);
  const pavimentoFotoInputRef = useRef(null);
  const topicoRefs = useRef({});

  // ── Rastreamento de edições não salvas (só descrições) ─────────────
  const [houveEdicao, setHouveEdicao] = useState(false);
  const [mostrarConfirmSair, setMostrarConfirmSair] = useState(false);
  const descricoesOriginaisRef = useRef({});

  const [todosPavimentosProjeto, setTodosPavimentosProjeto] = useState([]);
  const [mostrarPavimentosDisponiveis, setMostrarPavimentosDisponiveis] = useState(false);

  const [climaDia, setClimaDia] = useState([]);

  const [mostrarCalendario, setMostrarCalendario] = useState(false);

  // ── Aviso de empresa terceirizada não preenchida ────────────────────
  const [avisoEmpresaFechado, setAvisoEmpresaFechado] = useState(
    () => sessionStorage.getItem("rdo_aviso_empresa_fechado") === "1"
  );

  const fecharAvisoEmpresa = () => {
    setAvisoEmpresaFechado(true);
    sessionStorage.setItem("rdo_aviso_empresa_fechado", "1");
  };

  const clicandoSugestaoRef = useRef(false);
  const engenheiroSalvoRef = useRef(null);
  const carregadosRef = useRef({});

  const draftKey = `rdo_draft_${notaId}`;

  const pavimentosDisponiveis = useMemo(() => {
    const usados = new Set(pavimentosAtividades.map(item => item.pavimento));
    return todosPavimentosProjeto.filter(pav => !usados.has(pav));
  }, [pavimentosAtividades, todosPavimentosProjeto]);

  // --- Limite dinâmico de fotos por pavimento ---
  const pavimentosPreenchidos = useMemo(
    () => pavimentosAtividades.filter(item => item.descricao.trim() !== "").length,
    [pavimentosAtividades]
  );

  const limiteFotosPorPavimento = pavimentosPreenchidos >= 1 && pavimentosPreenchidos <= 3 ? 5 : 2;

  // --- Flag de layout do clima (novo vs. legado) ---
  const usaLayoutNovoClima = useMemo(() => {
    if (!rdoId || !rdoCreatedAt) return true;
    return new Date(rdoCreatedAt) >= new Date(DATA_CORTE_NOVO_LAYOUT_CLIMA + "T00:00:00");
  }, [rdoId, rdoCreatedAt]);

  // ── Flag do novo layout por data_entrega ────────────────────────────
  const usarNovoLayoutClima = dataOriginal >= DATA_CORTE_NOVO_LAYOUT_CLIMA;

  // ── Atividades em tópicos só a partir da data de corte ──────────────
  const usarTopicosAtividades = dataOriginal >= DATA_CORTE_TOPICOS_ATIVIDADES;

  const bloqueado = useMemo(() => {
    if (!dataOriginal) return false;
    const [ano, mes, dia] = dataOriginal.split("-").map(Number);
    const dataRdo = new Date(ano, mes - 1, dia);
    const diffDias = (Date.now() - dataRdo.getTime()) / (1000 * 60 * 60 * 24);
    return diffDias > 7;
  }, [dataOriginal]);

  const bloqueadoFuturo = useMemo(() => {
    if (!dataOriginal) return false;
    const [ano, mes, dia] = dataOriginal.split("-").map(Number);
    const dataRdo = new Date(ano, mes - 1, dia);
    dataRdo.setHours(0, 0, 0, 0);

    const limite = new Date();
    limite.setDate(limite.getDate() + 1);
    limite.setHours(0, 0, 0, 0);

    return dataRdo.getTime() > limite.getTime();
  }, [dataOriginal]);

  const edicaoBloqueada = bloqueado || bloqueadoFuturo;

  // Ordena os RDOs por data de entrega (data_entrega ?? data ?? "")
  const rdosOrdenados = useMemo(() => {
    return [...rdosDaPilha].sort((a, b) => {
      const da = (a?.data_entrega ?? a?.data ?? "") || "";
      const db = (b?.data_entrega ?? b?.data ?? "") || "";
      return String(da).localeCompare(String(db));
    });
  }, [rdosDaPilha]);

  // Recalcula o índice atual baseado no notaId (não confia no indexAtual do pai)
  const indexOrdenado = useMemo(
    () => rdosOrdenados.findIndex(r => (r?.id ?? r) === notaId),
    [rdosOrdenados, notaId]
  );

  const navegarRdoProximo = React.useCallback(() => {
    if (!rdosOrdenados.length || indexOrdenado === -1) return;
    const proximoIndex = (indexOrdenado + 1) % rdosOrdenados.length;
    const prox = rdosOrdenados[proximoIndex];
    if (prox && onNavegarRdo) onNavegarRdo(prox.id ?? prox);
  }, [rdosOrdenados, indexOrdenado, onNavegarRdo]);

  const navegarRdoAnterior = React.useCallback(() => {
    if (!rdosOrdenados.length || indexOrdenado === -1) return;
    const anteriorIndex = (indexOrdenado - 1 + rdosOrdenados.length) % rdosOrdenados.length;
    const ant = rdosOrdenados[anteriorIndex];
    if (ant && onNavegarRdo) onNavegarRdo(ant.id ?? ant);
  }, [rdosOrdenados, indexOrdenado, onNavegarRdo]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.target.isContentEditable) return;

      if (e.key === "ArrowRight") { e.preventDefault(); navegarRdoProximo(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); navegarRdoAnterior(); }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [navegarRdoProximo, navegarRdoAnterior]);

  // Fecha o calendário ao clicar fora
  useEffect(() => {
    if (!mostrarCalendario) return;
    const fn = (e) => {
      if (!e.target.closest(".rdo-calendario-dropdown") && !e.target.closest(".rdo-nav-btn")) {
        setMostrarCalendario(false);
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [mostrarCalendario]);

  // ── Detecta edição comparando descrição atual vs. original ─────────
  useEffect(() => {
    if (loadingInicial) return;
    const original = descricoesOriginaisRef.current;
    const mudou = pavimentosAtividades.some(
      (item) => (item.descricao || "") !== (original[item.pavimento] || "")
    );
    setHouveEdicao(mudou);
  }, [pavimentosAtividades, loadingInicial]);

  const temMultiplosRdos = rdosOrdenados.length > 1;

  useEffect(() => {
    const fetchProjetoData = async () => {
      if (!projetoAtual?.id) return;
      const { data: proj, error } = await supabase
        .from("projects")
        .select("name, engenheiro_id, data_inicio, data_finalizacao, photo_url")
        .eq("id", projetoAtual.id)
        .single();

      if (error || !proj) return;

      setProjetoNome(proj.name);

      // ── Resolve a URL da imagem (aceita URL completa, data URI ou path do Storage) ──
      let urlImagem = proj.photo_url || "";
      if (urlImagem && !/^https?:\/\//i.test(urlImagem) && !urlImagem.startsWith("data:")) {
        const { data: pub } = supabase.storage.from("NOME_DO_BUCKET").getPublicUrl(urlImagem);
        urlImagem = pub?.publicUrl || "";
      }
      setProjetoImagem(urlImagem);

      setData((prev) => ({
        ...prev,
        inicio_obra: prev.inicio_obra || proj.data_inicio || "",
        termino_obra: prev.termino_obra || proj.data_finalizacao || "",
      }));

      if (engenheiroSalvoRef.current) return;

      if (proj.engenheiro_id) {
        const { data: user } = await supabase
          .from("profiles")
          .select("nome")
          .eq("id", proj.engenheiro_id)
          .single();
        setEngenheiroNome(user?.nome || "Não atribuído");
      } else {
        setEngenheiroNome("Não atribuído");
      }
    };
    fetchProjetoData();
  }, [projetoAtual?.id, rdoCarregado]);

  useEffect(() => {
    const fetchClima = async () => {
      if (!projetoAtual?.id || !dataOriginal) return;
      const { data: clima } = await supabase
        .from("clima_diario")
        .select("periodo, condicao, temperatura, icone")
        .eq("project_id", projetoAtual.id)
        .eq("data", dataOriginal);
      setClimaDia(clima || []);
    };
    fetchClima();
  }, [projetoAtual?.id, dataOriginal]);

  const climaPorPeriodo = (periodo) => climaDia.find((c) => c.periodo === periodo);

  // Mapeia o código de ícone da OpenWeather para um ícone e cor
  const getIconeClima = (codigoIcone) => {
    if (!codigoIcone) return null;
    const prefixo = codigoIcone.slice(0, 2);
    const ehNoite = codigoIcone.endsWith("n");

    const mapa = {
      "01": ehNoite ? { icon: faMoon, color: "#5c6bc0" } : { icon: faSun, color: "#f5a623" },
      "02": ehNoite ? { icon: faCloudMoon, color: "#5c6bc0" } : { icon: faCloudSun, color: "#f5a623" },
      "03": { icon: faCloud, color: "#90a4ae" },
      "04": { icon: faCloud, color: "#78909c" },
      "09": { icon: faCloudShowersHeavy, color: "#4a90d9" },
      "10": { icon: faCloudShowersHeavy, color: "#2b6cb0" },
      "11": { icon: faBolt, color: "#f6c744" },
      "13": { icon: faSnowflake, color: "#81d4fa" },
      "50": { icon: faSmog, color: "#b0bec5" },
    };
    return mapa[prefixo] || null;
  };

  useEffect(() => {
    const fetchRdo = async () => {
      if (!notaId) return;

      setHouveEdicao(false);

      if (carregadosRef.current[notaId]) {
        setLoadingInicial(false);
        return;
      }

      setLoadingInicial(true);

      try {
        const { data: nota } = await supabase
          .from("notas")
          .select("data_entrega")
          .eq("id", notaId)
          .single();

        const dataEntrega = nota?.data_entrega ? nota.data_entrega.split("T")[0] : "";
        setDataOriginal(dataEntrega);

        let diaSemana = "";
        if (dataEntrega) {
          const [ano, mes, dia] = dataEntrega.split("-").map(Number);
          const dataLocal = new Date(ano, mes - 1, dia);
          const dias = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];
          diaSemana = dias[dataLocal.getDay()];
        }
        setDiaSemanaOriginal(diaSemana);

        const { data: rdoData } = await supabase
          .from("rdos")
          .select("*")
          .eq("nota_id", notaId)
          .maybeSingle();

        const { data: pavimentosData } = await supabase
          .from("pavimentos")
          .select("name")
          .eq("project_id", projetoAtual.id)
          .order("ordem", { ascending: true });

        const nomesPavimentos = pavimentosData.map(p => p.name);
        setTodosPavimentosProjeto(nomesPavimentos);

        const fotosPorPavimento = rdoData?.atividades_fotos || {};
        const topicosPorPavimento = rdoData?.atividades_topicos || {};
        let pavimentosComDescricao = [];
        if (rdoData?.atividades && Object.keys(rdoData.atividades).length > 0) {
          pavimentosComDescricao = Object.keys(rdoData.atividades).map((p) => ({
            pavimento: p,
            descricao: rdoData.atividades[p] || "",
            topicos: montarTopicos(rdoData.atividades[p], topicosPorPavimento[p]),
            fotos: (fotosPorPavimento[p] || []).map((f) => ({ ...f, isNew: false })),
          }));
        } else {
          pavimentosComDescricao = nomesPavimentos.map((p) => ({
            pavimento: p, descricao: "", topicos: [TOPICO_VAZIO()], fotos: [],
          }));
        }
        setPavimentosAtividades(pavimentosComDescricao);

        // ── Snapshot das descrições originais ──────────────────────
        descricoesOriginaisRef.current = Object.fromEntries(
          pavimentosComDescricao.map((item) => [item.pavimento, item.descricao])
        );
        setHouveEdicao(false);

        const jaExisteNoBanco = !!rdoData;

        if (rdoData) {
          setRdoId(rdoData.id);
          setRdoCreatedAt(rdoData.created_at || null);
          setRdoCarregado(true);

          const temIntercorrencias = rdoData.tem_intercorrencias !== undefined
            ? rdoData.tem_intercorrencias
            : (rdoData.intercorrencias && rdoData.intercorrencias.trim() !== "");

          setData({
            inicio_obra: rdoData.inicio_obra || "",
            termino_obra: rdoData.termino_obra || "",
            atraso_dias: rdoData.atraso_dias || "",
            feriado: rdoData.feriado || false,
            dia_compensado: rdoData.dia_compensado || false,
            clima_manha: rdoData.clima_manha || "",
            clima_tarde: rdoData.clima_tarde || "",
            obra_op_manha: rdoData.obra_op_manha || "",
            obra_op_tarde: rdoData.obra_op_tarde || "",
            efetivo_proprio: Array.isArray(rdoData.efetivo_proprio) ? rdoData.efetivo_proprio : [{ funcao: "", total: "", presentes: "" }],
            efetivo_terceirizado: Array.isArray(rdoData.efetivo_terceirizado) ? rdoData.efetivo_terceirizado : [{ funcao: "", total: "", presentes: "" }],
            recebimento_materiais: Array.isArray(rdoData.recebimento_materiais) ? rdoData.recebimento_materiais : [{ codigo: "", descricao: "", unidade: "", quantidade: "", empresa: "" }],
            equipamentos: Array.isArray(rdoData.equipamentos) ? rdoData.equipamentos : [{ codigo: "", descricao: "", total: "", em_uso: "" }],
            intercorrencias: rdoData.intercorrencias || "",
            tem_intercorrencias: temIntercorrencias,
            responsavel_preenchimento: rdoData.responsavel_preenchimento || "",
            fotos: Array.isArray(rdoData.fotos) ? rdoData.fotos : [],
          });
          if (rdoData.projeto_nome) setProjetoNome(rdoData.projeto_nome);

          if (rdoData.engenheiro_nome) {
            engenheiroSalvoRef.current = rdoData.engenheiro_nome;
            setEngenheiroNome(rdoData.engenheiro_nome);
          }
        } else {
          setRdoCreatedAt(null);
          setRdoCarregado(true);
        }

        if (jaExisteNoBanco) {
          const savedDraft = sessionStorage.getItem(draftKey);
          if (savedDraft) {
            try {
              const draft = JSON.parse(savedDraft);

              if (draft.data) {
                const fotosRestauradas = (draft.data.fotos || []).map((foto) => {
                  if (typeof foto === "string") return foto;
                  if (foto?.__base64) return base64ParaFile(foto.data, foto.nome);
                  return foto;
                });
                setData({ ...draft.data, fotos: fotosRestauradas });
              }

              if (draft.pavimentosAtividades) {
                const pavimentosRestaurados = draft.pavimentosAtividades.map((item) => {
                  if (!item.fotos?.length) return item;
                  const fotos = item.fotos.map((foto) => {
                    if (foto?.__base64) {
                      const file = base64ParaFile(foto.data, foto.nome);
                      return { file, legenda: foto.legenda || "", isNew: true, previewUrl: URL.createObjectURL(file) };
                    }
                    return foto;
                  });
                  return { ...item, fotos };
                });
                setPavimentosAtividades(pavimentosRestaurados);
              }
            } catch (e) {
              console.warn("Falha ao restaurar rascunho do RDO", e);
            }
          }
        }

        carregadosRef.current[notaId] = true;

      } catch (err) {
        console.error("Erro ao carregar RDO:", err);
      } finally {
        setLoadingInicial(false);
      }
    };
    fetchRdo();
  }, [notaId, projetoAtual?.id]);

  useEffect(() => {
    if (!notaId || !rdoCarregado) return;

    const salvarDraft = async () => {
      try {
        const fotosSerializadas = await Promise.all(
          data.fotos.map(async (foto, i) => {
            if (typeof foto === "string") return foto;
            return {
              __base64: true,
              nome: (foto.name || `foto-${i}.jpg`).replace(/\.\w+$/, ".jpg"),
              data: await comprimirImagemParaBase64(foto),
            };
          })
        );

        const pavimentosSerializados = await Promise.all(
          pavimentosAtividades.map(async (item) => {
            if (!item.fotos?.length) return item;
            const fotos = await Promise.all(
              item.fotos.map(async (foto) => {
                if (!foto.isNew) return foto;
                return {
                  isNew: true,
                  legenda: foto.legenda || "",
                  __base64: true,
                  nome: (foto.file?.name || "foto.jpg").replace(/\.\w+$/, ".jpg"),
                  data: await comprimirImagemParaBase64(foto.file),
                };
              })
            );
            return { ...item, fotos };
          })
        );

        const draft = {
          data: { ...data, fotos: fotosSerializadas },
          pavimentosAtividades: pavimentosSerializados,
          timestamp: Date.now(),
        };
        sessionStorage.setItem(draftKey, JSON.stringify(draft));
      } catch (e) {
        console.warn("Falha ao salvar rascunho do RDO (provavelmente cota excedida)", e);
      }
    };

    salvarDraft();
  }, [data, pavimentosAtividades, notaId, rdoCarregado, draftKey]);

  const copiarDoUltimoRdo = async (tipo) => {
  if (edicaoBloqueada || !projetoAtual?.id) return;
  try {
    let query = supabase
      .from("rdos")
      .select("*")
      .eq("project_id", projetoAtual.id)
      .order("data_rdo", { ascending: false })
      .limit(1);

    if (rdoId) query = query.neq("id", rdoId);

    // Para atividades: só considera RDOs ANTERIORES à data atual
    // e que realmente tenham atividades preenchidas
    if (tipo === "atividades") {
      if (dataOriginal) query = query.lt("data_rdo", dataOriginal);
    }

    const { data: rdos } = await query;
    if (!rdos?.[0]) {
      alert("Nenhum Diário de Obra anterior encontrado para copiar.");
      return;
    }
    const ultimo = rdos[0];
    let dados = [];
    if (tipo === "efetivo_proprio") dados = ultimo.efetivo_proprio || [{ funcao: "", total: "", presentes: "" }];
    if (tipo === "efetivo_terceirizado") dados = ultimo.efetivo_terceirizado || [{ funcao: "", total: "", presentes: "" }];
    if (tipo === "recebimento_materiais") dados = ultimo.recebimento_materiais || [{ codigo: "", descricao: "", unidade: "", quantidade: "", empresa: "" }];
    if (tipo === "equipamentos") dados = ultimo.equipamentos || [{ codigo: "", descricao: "", total: "", em_uso: "" }];

    if (tipo === "atividades") {
      const atividadesAnteriores = ultimo.atividades || {};

      if (Object.keys(atividadesAnteriores).length === 0) {
        alert("O Diário de Obra anterior não tem atividades preenchidas.");
        return;
      }

      // Mantém TODOS os pavimentos do projeto; só preenche os que existiam antes
      const base = todosPavimentosProjeto.length
        ? todosPavimentosProjeto
        : Object.keys(atividadesAnteriores);

      const topicosAnteriores = ultimo.atividades_topicos || {};
      const novasAtividades = base.map((pav) => ({
        pavimento: pav,
        descricao: atividadesAnteriores[pav] || "",
        topicos: montarTopicos(atividadesAnteriores[pav], topicosAnteriores[pav]),
        fotos: [],
      }));

      setPavimentosAtividades(novasAtividades);
      return;
    }

    setData((prev) => ({ ...prev, [tipo]: dados }));
  } catch (err) {
    alert("Erro ao copiar dados do último Diário de Obra.");
  }
};

  const buscarMembrosDoProjetoRdo = async (termo) => {
    if (!projetoAtual?.id) return [];

    const { data: membros, error: erroMembros } = await supabase
      .from("project_members")
      .select("user_id")
      .eq("project_id", projetoAtual.id);

    if (erroMembros || !membros?.length) return [];

    const ids = membros.map(m => m.user_id).filter(Boolean);
    if (ids.length === 0) return [];

    let query = supabase
      .from("profiles")
      .select("id, nome, nickname, avatar_url")
      .in("id", ids)
      .limit(8);

    if (termo.trim()) {
      query = query.or(
        `nome.ilike.%${termo}%,nickname.ilike.%${termo}%`
      );
    }

    const { data: perfis, error: erroPerfis } = await query;
    if (erroPerfis) {
      console.error("Erro ao buscar membros:", erroPerfis);
      return [];
    }

    return perfis || [];
  };

  const handleResponsavelChange = async (e) => {
    const valor = e.target.value;
    const pos = e.target.selectionStart;
    setData(prev => ({ ...prev, responsavel_preenchimento: valor }));

    const antes = valor.substring(0, pos);
    const arroba = antes.lastIndexOf("@");

    if (arroba !== -1) {
      const termo = antes.substring(arroba + 1);
      setMostrarSugestoesMembros(true);
      const res = await buscarMembrosDoProjetoRdo(termo);
      setSugestoesMembros(res);
    } else {
      setMostrarSugestoesMembros(false);
      setSugestoesMembros([]);
    }
  };

  const inserirResponsavel = (perfil) => {
    const nomeEscolhido = perfil.nickname || perfil.nome || "";
    const valorAtual = data.responsavel_preenchimento;
    const pos = responsavelInputRef.current?.selectionStart ?? valorAtual.length;

    const antes = valorAtual.substring(0, pos);
    const depois = valorAtual.substring(pos);
    const arroba = antes.lastIndexOf("@");

    const novoValor = antes.substring(0, arroba) + nomeEscolhido + depois;

    setData(prev => ({ ...prev, responsavel_preenchimento: novoValor }));
    setMostrarSugestoesMembros(false);
    setSugestoesMembros([]);
    clicandoSugestaoRef.current = false;

    setTimeout(() => {
      if (responsavelInputRef.current) {
        const novaCursor = arroba + nomeEscolhido.length;
        responsavelInputRef.current.focus();
        responsavelInputRef.current.setSelectionRange(novaCursor, novaCursor);
      }
    }, 0);
  };

  const updateField = (field, value) => {
    if (edicaoBloqueada) return;
    setData(prev => ({ ...prev, [field]: value }));
  };

  const updateArrayField = (arrayKey, index, field, value) => {
    if (edicaoBloqueada) return;
    setData(prev => {
      const arr = [...prev[arrayKey]];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [arrayKey]: arr };
    });
  };

  const addRow = (key) => {
    if (edicaoBloqueada) return;
    let row;
    if (key === "equipamentos") {
      row = { codigo: "", descricao: "", total: "", em_uso: "" };
    } else if (key === "recebimento_materiais") {
      row = { codigo: "", descricao: "", unidade: "", quantidade: "", empresa: "" };
    } else {
      row = { funcao: "", total: "", presentes: "" };
    }
    setData(prev => ({ ...prev, [key]: [...prev[key], row] }));
  };

  const removeRow = (key, idx) => {
    if (edicaoBloqueada) return;
    setData(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
  };

  const updatePavimentoDescricao = (pav, desc) => {
    if (edicaoBloqueada) return;
    setPavimentosAtividades(prev =>
      prev.map(item => item.pavimento === pav ? { ...item, descricao: desc } : item)
    );
  };

  // ── Tópicos das atividades ──────────────────────────────────────
  const aplicarTopicos = (pav, fn) => {
    if (edicaoBloqueada) return;
    setPavimentosAtividades((prev) =>
      prev.map((item) => {
        if (item.pavimento !== pav) return item;
        const topicos = fn(item.topicos || [TOPICO_VAZIO()]);
        return { ...item, topicos, descricao: juntarTopicos(topicos) };
      })
    );
  };

  const updateTopico = (pav, i, campo, valor) =>
    aplicarTopicos(pav, (ts) => ts.map((t, idx) => (idx === i ? { ...t, [campo]: valor } : t)));

  const addTopico = (pav, depoisDe) => {
    aplicarTopicos(pav, (ts) => {
      const novo = [...ts];
      novo.splice(depoisDe + 1, 0, TOPICO_VAZIO());
      return novo;
    });
    setTimeout(() => topicoRefs.current[`${pav}-${depoisDe + 1}`]?.focus(), 0);
  };

  const removeTopico = (pav, i) =>
    aplicarTopicos(pav, (ts) => (ts.length <= 1 ? [TOPICO_VAZIO()] : ts.filter((_, idx) => idx !== i)));

  const removerPavimentoDoRdo = (pav) => {
    if (edicaoBloqueada) return;
    setPavimentosAtividades(prev => prev.filter(item => item.pavimento !== pav));
  };

  const abrirSeletorFotoPavimento = (pavimento) => {
    if (edicaoBloqueada) return;
    const item = pavimentosAtividades.find(i => i.pavimento === pavimento);
    if ((item?.fotos?.length || 0) >= limiteFotosPorPavimento) {
      alert(`Máximo de ${limiteFotosPorPavimento} fotos por pavimento.`);
      return;
    }
    setPavimentoFotoAlvo(pavimento);
    pavimentoFotoInputRef.current?.click();
  };

  const handlePavimentoFotoSelecionada = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !pavimentoFotoAlvo || edicaoBloqueada) { setPavimentoFotoAlvo(null); return; }
    setPavimentosAtividades(prev => prev.map(item => {
      if (item.pavimento !== pavimentoFotoAlvo) return item;
      const vagas = limiteFotosPorPavimento - (item.fotos?.length || 0);
      const arquivosPermitidos = files.slice(0, Math.max(vagas, 0));
      const novasFotos = arquivosPermitidos.map(file => ({
        file,
        legenda: "",
        isNew: true,
        previewUrl: URL.createObjectURL(file),
      }));
      return { ...item, fotos: [...(item.fotos || []), ...novasFotos] };
    }));
    setPavimentoFotoAlvo(null);
  };

  const removerFotoPavimento = (pavimento, idx) => {
    if (edicaoBloqueada) return;
    if (!window.confirm("Remover esta foto?")) return;
    setPavimentosAtividades(prev => prev.map(item =>
      item.pavimento === pavimento
        ? { ...item, fotos: item.fotos.filter((_, i) => i !== idx) }
        : item
    ));
  };

  const atualizarLegendaFotoPavimento = (pavimento, idx, legenda) => {
    if (edicaoBloqueada) return;
    setPavimentosAtividades(prev => prev.map(item =>
      item.pavimento === pavimento
        ? { ...item, fotos: item.fotos.map((f, i) => i === idx ? { ...f, legenda } : f) }
        : item
    ));
  };

  const abrirHistoricoPavimento = async (pavimento) => {
    if (!projetoAtual?.id) return;
    setBuscaHistorico("");
    setHistoricoPavimento({ pavimento, itens: [] });
    setCarregandoHistorico(true);
    try {
      const { data: rdos } = await supabase
        .from("rdos")
        .select("id, data_rdo, atividades, atividades_fotos")
        .eq("project_id", projetoAtual.id)
        .order("data_rdo", { ascending: false });

      const itens = (rdos || [])
        .filter((r) => r.atividades?.[pavimento]?.trim())
        .map((r) => ({
          id: r.id,
          data: r.data_rdo,
          descricao: r.atividades[pavimento],
          fotos: r.atividades_fotos?.[pavimento] || [],
        }));

      setHistoricoPavimento({ pavimento, itens });
    } catch (err) {
      console.error("Erro ao carregar histórico do pavimento:", err);
      setHistoricoPavimento({ pavimento, itens: [] });
    } finally {
      setCarregandoHistorico(false);
    }
  };

  const buscarItemPorCodigo = async (idx, cod) => {
    if (!cod?.trim()) {
      setData(prev => {
        const eq = [...prev.equipamentos];
        eq[idx].descricao = "";
        return { ...prev, equipamentos: eq };
      });
      return;
    }
    const { data: item } = await supabase
      .from("itens")
      .select("descricao")
      .eq("codigo", cod)
      .maybeSingle();
    setData(prev => {
      const eq = [...prev.equipamentos];
      eq[idx].descricao = item?.descricao || "";
      return { ...prev, equipamentos: eq };
    });
  };

  const buscarMaterialPorCodigo = async (idx, cod) => {
    if (!cod?.trim()) {
      setData(prev => {
        const mat = [...prev.recebimento_materiais];
        mat[idx].descricao = "";
        mat[idx].unidade = "";
        return { ...prev, recebimento_materiais: mat };
      });
      return;
    }
    const { data: item } = await supabase
      .from("itens")
      .select("descricao, unidade")
      .eq("codigo", cod)
      .maybeSingle();
    setData(prev => {
      const mat = [...prev.recebimento_materiais];
      mat[idx].descricao = item?.descricao || "";
      mat[idx].unidade = item?.unidade || "";
      return { ...prev, recebimento_materiais: mat };
    });
  };

  const abrirBuscaInsumo = (idx, tipo) => {
    setLinhaBuscaAtiva(idx);
    setTipoInsumoAtivo(tipo);
    setBuscaInsumoAberta(true);
  };

  const handleSelecionarInsumo = (cod) => {
    if (linhaBuscaAtiva !== null && tipoInsumoAtivo) {
      updateArrayField(tipoInsumoAtivo, linhaBuscaAtiva, "codigo", cod);

      if (tipoInsumoAtivo === "equipamentos") {
        buscarItemPorCodigo(linhaBuscaAtiva, cod);
      } else if (tipoInsumoAtivo === "recebimento_materiais") {
        buscarMaterialPorCodigo(linhaBuscaAtiva, cod);
      }
    }
    setBuscaInsumoAberta(false);
    setLinhaBuscaAtiva(null);
    setTipoInsumoAtivo(null);
  };

  // ── Lista de empresas disponíveis (efetivo terceirizado + PRÓPRIO) ──
  const empresasDisponiveis = useMemo(() => {
    const nomes = (data.efetivo_terceirizado || [])
      .map((e) => (e.funcao || "").trim())
      .filter(Boolean);
    return ["PRÓPRIO", ...Array.from(new Set(nomes.map((n) => n.toUpperCase())))];
  }, [data.efetivo_terceirizado]);

  // Só "PRÓPRIO" na lista = nenhuma empresa terceirizada preenchida
  const semEmpresasTerceirizadas = empresasDisponiveis.length <= 1;

  const saveRdo = async () => {
    if (bloqueado) {
      alert("Este Diário de Obra tem mais de 7 dias e não pode mais ser editado.");
      return;
    }
    if (bloqueadoFuturo) {
      alert("Este Diário de Obra ainda não pode ser preenchido — disponível apenas a partir de amanhã.");
      return;
    }
        if (!notaId || !projetoAtual?.id || !usuarioId) {
      alert("Dados insuficientes para salvar o RDO.");
      return;
    }
    const temAtividadePreenchida = pavimentosAtividades.some(item => item.descricao.trim() !== "");
    if (temAtividadePreenchida && !data.responsavel_preenchimento.trim()) {
      alert("Preencha o Responsável pelo Preenchimento antes de salvar.");
      return;
    }
    setLoading(true);
    setLoading(true);
    try {
      let fotosUrls = [];
      if (data.fotos.length > 0) {
        const uploads = data.fotos.map(async (foto, i) => {
          if (typeof foto === "string") return foto;
          const ext = (foto.name?.split(".").pop() || "jpg").toLowerCase();
          const name = `rdo-${notaId}-${Date.now()}-${i}.${ext}`;
          const path = `rdo-fotos/${notaId}/${name}`;
          const { error } = await supabase.storage.from("rdo-fotos").upload(path, foto);
          if (error) throw error;
          const { data: { publicUrl } } = supabase.storage.from("rdo-fotos").getPublicUrl(path);
          return publicUrl;
        });
        fotosUrls = await Promise.all(uploads);
      }

      const atividadesFotos = {};
      for (const item of pavimentosAtividades) {
        if (!item.fotos?.length) continue;
        const fotosDoItem = await Promise.all(item.fotos.map(async (foto, i) => {
          if (!foto.isNew) return { url: foto.url, legenda: foto.legenda || "" };
          const ext = (foto.file.name?.split(".").pop() || "jpg").toLowerCase();
          const name = `rdo-${notaId}-pav-${slugify(item.pavimento)}-${Date.now()}-${i}.${ext}`;
          const path = `rdo-fotos/${notaId}/${name}`;
          const { error } = await supabase.storage.from("rdo-fotos").upload(path, foto.file);
          if (error) throw error;
          const { data: { publicUrl } } = supabase.storage.from("rdo-fotos").getPublicUrl(path);
          return { url: publicUrl, legenda: foto.legenda || "" };
        }));
        atividadesFotos[item.pavimento] = fotosDoItem;
      }

      const atividades = {};
      pavimentosAtividades.forEach(item => {
        if (item.descricao.trim()) atividades[item.pavimento] = item.descricao.trim();
      });

      // ── Monta os tópicos por pavimento (só se o layout novo estiver ativo) ──
      const atividadesTopicos = {};
      if (usarTopicosAtividades) {
        pavimentosAtividades.forEach((item) => {
          const ts = (item.topicos || []).filter((t) => t.texto.trim());
          if (ts.length) {
            atividadesTopicos[item.pavimento] = ts.map((t) => ({
              texto: t.texto.trim(),
              empresa: t.empresa || "",
            }));
          }
        });
      }

      const payload = {
        nota_id: notaId,
        project_id: projetoAtual.id,
        data_rdo: dataOriginal || null,
        dia_semana: diaSemanaOriginal || "",
        projeto_nome: projetoNome || "",
        engenheiro_nome: engenheiroNome || "",
        inicio_obra: data.inicio_obra || null,
        termino_obra: data.termino_obra || null,
        atraso_dias: data.atraso_dias || "",
        feriado: data.feriado || false,
        dia_compensado: data.dia_compensado || false,
        clima_manha: data.clima_manha || "",
        clima_tarde: data.clima_tarde || "",
        obra_op_manha: data.obra_op_manha || "",
        obra_op_tarde: data.obra_op_tarde || "",
        efetivo_proprio: data.efetivo_proprio || [],
        efetivo_terceirizado: data.efetivo_terceirizado || [],
        recebimento_materiais: data.recebimento_materiais || [],
        equipamentos: data.equipamentos || [],
        atividades,
        ...(usarTopicosAtividades ? { atividades_topicos: atividadesTopicos } : {}),
        atividades_fotos: atividadesFotos,
        intercorrencias: data.tem_intercorrencias ? data.intercorrencias : "",
        tem_intercorrencias: data.tem_intercorrencias,
        responsavel_preenchimento: data.responsavel_preenchimento || "",
        fotos: fotosUrls,
        created_by: usuarioId,
      };

      if (rdoId) {
        const { data: atualizado, error } = await supabase
          .from("rdos")
          .update(payload)
          .eq("id", rdoId)
          .select();
        if (error) throw error;
        if (!atualizado?.length) {
          throw new Error("Nada foi atualizado. Verifique as permissões (RLS) da tabela rdos.");
        }
      } else {
        const { data: res, error } = await supabase
          .from("rdos")
          .insert([payload])
          .select();
        if (error) throw error;
        if (!res?.[0]?.id) {
          throw new Error("O RDO não foi gravado. Verifique as permissões (RLS) da tabela rdos.");
        }
        setRdoId(res[0].id);
        setRdoCreatedAt(res[0].created_at || new Date().toISOString());
      }

      // Notifica o calendário para pintar o dia como preenchido / especial
      if (dataOriginal) {
        window.dispatchEvent(new CustomEvent("rdoSalvo", {
          detail: {
            dataStr: dataOriginal,
            feriado: data.feriado || false,
            dia_compensado: data.dia_compensado || false,
          }
        }));
      }

      // Atualiza o snapshot e zera o rastreio
      descricoesOriginaisRef.current = Object.fromEntries(
        pavimentosAtividades.map((item) => [item.pavimento, item.descricao])
      );
      setHouveEdicao(false);

      sessionStorage.removeItem(draftKey);
      alert("Diário de Obra salvo com sucesso!");
      onClose();
    } catch (error) {
      console.error("Erro ao salvar RDO:", error);
      alert(`Erro ao salvar: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Salva imediatamente feriado / dia_compensado (ignora bloqueio de 7 dias) ──
  const atualizarCampoEspecial = async (campo, valor) => {
    setData(prev => ({ ...prev, [campo]: valor }));
    if (!notaId || !projetoAtual?.id) return;

    try {
      if (rdoId) {
        const { error } = await supabase.from("rdos").update({ [campo]: valor }).eq("id", rdoId);
        if (error) throw error;
      } else {
        const payload = {
          nota_id: notaId,
          project_id: projetoAtual.id,
          data_rdo: dataOriginal || null,
          dia_semana: diaSemanaOriginal || "",
          projeto_nome: projetoNome || "",
          engenheiro_nome: engenheiroNome || "",
          feriado: campo === "feriado" ? valor : (data.feriado || false),
          dia_compensado: campo === "dia_compensado" ? valor : (data.dia_compensado || false),
          created_by: usuarioId,
        };
        const { data: res, error } = await supabase.from("rdos").insert([payload]).select();
        if (error) throw error;
        if (res?.[0]?.id) {
          setRdoId(res[0].id);
          setRdoCreatedAt(res[0].created_at || new Date().toISOString());
        }
      }

      if (dataOriginal) {
        window.dispatchEvent(new CustomEvent("rdoSalvo", {
          detail: {
            dataStr: dataOriginal,
            feriado: campo === "feriado" ? valor : data.feriado,
            dia_compensado: campo === "dia_compensado" ? valor : data.dia_compensado,
          }
        }));
      }
    } catch (err) {
      console.error("Erro ao salvar campo especial:", err);
      alert("Erro ao salvar. Tente novamente.");
    }
  };

  const handleCancel = () => {
    sessionStorage.removeItem(draftKey);
    onClose();
  };

  // ── Clique no X com verificação de edições não salvas ───────────
  const handleFecharClique = () => {
    if (houveEdicao && !edicaoBloqueada) {
      setMostrarConfirmSair(true);
    } else {
      onClose && onClose();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [ano, mes, dia] = dateStr.split("-").map(Number);
    return `${String(dia).padStart(2, "0")}/${String(mes).padStart(2, "0")}/${ano}`;
  };

  const destacarTexto = (texto, termo) => {
    if (!termo.trim()) return texto;
    const escapado = termo.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const partes = texto.split(new RegExp(`(${escapado})`, "gi"));
    return partes.map((parte, i) =>
      i % 2 === 1 ? (
        <mark key={i} className="rdo-historico-highlight">{parte}</mark>
      ) : (
        <React.Fragment key={i}>{parte}</React.Fragment>
      )
    );
  };

  // ── NOVO: monta o pacote de dados que vai pro PDF ────────────────
  const montarDadosPdf = () => ({
    titulo: `Diário de obra - ${projetoNome || "Projeto não informado"}`,
    subtitulo: dataOriginal
      ? `${formatDate(dataOriginal)} – ${diaSemanaOriginal}`
      : "(Data não definida)",
    dataRdo: dataOriginal,
    engenheiro: engenheiroNome,
    responsavel: data.responsavel_preenchimento,
    inicioObra: data.inicio_obra,
    terminoObra: data.termino_obra,
    atraso: data.atraso_dias,
    feriado: data.feriado,
    diaCompensado: data.dia_compensado,

    usarNovoLayoutClima,
    clima: [
      { key: "manha", hora: "10h" },
      { key: "tarde", hora: "15h" },
      { key: "noite", hora: "19h" },
    ].map(({ key, hora }) => {
      const r = climaPorPeriodo(key);
      return { hora, condicao: r?.condicao, temperatura: r?.temperatura };
    }),
    climaManha: data.clima_manha,
    climaTarde: data.clima_tarde,
    obraOpManha: data.obra_op_manha,
    obraOpTarde: data.obra_op_tarde,

    atividades: pavimentosAtividades.map((item) => ({
      pavimento: item.pavimento,
      descricao: item.descricao,
      topicos: usarTopicosAtividades
        ? (item.topicos || []).filter((t) => t.texto.trim())
        : [],
      fotos: (item.fotos || []).map((f) => ({
        src: f.isNew ? f.previewUrl : f.url,
        legenda: f.legenda || "",
      })),
    })),

    efetivoProprio: data.efetivo_proprio,
    efetivoTerc: data.efetivo_terceirizado,
    materiais: data.recebimento_materiais,
    equipamentos: data.equipamentos,
    intercorrencias: data.tem_intercorrencias ? data.intercorrencias : "",
    fotos: data.fotos.map((f) => (typeof f === "string" ? f : URL.createObjectURL(f))),
  });

  return (
    <div className={`rdo-modal-container${telaCheia ? " rdo-modal-container--tela-cheia" : ""}`}>
      <div className="listagem-card">
        <div className="listagem-header-container">
                    <div className="listagem-header-titles">
                      {loadingInicial ? (
                        <>
                          <div className="sk sk-header-title"></div>
                          <div className="sk sk-header-subtitle"></div>
                        </>
                      ) : (
                        <>
                          <span className="project-name">
                            Diário de obra - {projetoNome || "Projeto não informado"}
                          </span>
                          <div className="sub-info">
                            <span className="nota-name1">
                              {dataOriginal ? `${formatDate(dataOriginal)} – ${diaSemanaOriginal}` : "(Data não definida)"}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {temMultiplosRdos && (
              <div className="rdo-nav-group">
                <button
                  className="rdo-nav-btn"
                  onClick={navegarRdoAnterior}
                  title="RDO anterior (←)"
                  disabled={rdosOrdenados.length <= 1}
                >
                  <FaChevronLeft size={14} />
                </button>
                <div style={{ position: "relative" }}>
                  <button
                    className="rdo-nav-btn"
                    onClick={() => setMostrarCalendario(v => !v)}
                    title="Escolher data"
                  >
                    <FaCalendarAlt size={14} />
                  </button>

                  {mostrarCalendario && (
                    <div className="rdo-calendario-dropdown" onClick={(e) => e.stopPropagation()}>
                      <CalendarioDiarioObra
                        pilhaId={pilhaId}
                        usuarioId={usuarioId}
                        onSelectNota={(id) => {
                          setMostrarCalendario(false);
                          if (onNavegarRdo) onNavegarRdo(id);
                        }}
                      />
                    </div>
                  )}
                </div>
                <button
                  className="rdo-nav-btn"
                  onClick={navegarRdoProximo}
                  title="Próximo RDO (→)"
                  disabled={rdosOrdenados.length <= 1}
                >
                  <FaChevronRight size={14} />
                </button>
              </div>
            )}
            <button
              className="listagem-close-btn"
              onClick={() => setTelaCheia((v) => !v)}
              title={telaCheia ? "Restaurar modal" : "Tela cheia"}
              aria-label={telaCheia ? "Restaurar modal" : "Tela cheia"}
            >
              {telaCheia ? <FaCompress /> : <FaExpand />}
            </button>
            {onClose && (
              <button className="listagem-close-btn" onClick={handleFecharClique} aria-label="Fechar">
                <FaTimes />
              </button>
            )}
          </div>
        </div>
      </div>

      {loadingInicial ? (
        <RdoSkeleton />
      ) : (
        <div className="rdo-content">
          {edicaoBloqueada && (
            <div className="rdo-bloqueado-aviso">
              {bloqueadoFuturo
                ? "Este Diário de Obra ainda não pode ser preenchido — disponível apenas a partir de amanhã."
                : "Este Diário de Obra tem mais de 7 dias e não pode mais ser editado."}
            </div>
          )}

          {/* ── FORA do fieldset: engenheiro/responsável + datas + checkboxes ── */}

          <div className="rdo-section">
            <div className="rdo-row">
              <div className="rdo-col">
                <div className="rdo-engenheiro-wrap">
                  {projetoImagem && (
                    <img src={projetoImagem} alt={projetoNome} className="rdo-projeto-img" />
                  )}
                  <div className="rdo-engenheiro-campo">
                    <label>Engenheiro Responsável</label>
                    <input
                      type="text"
                      value={engenheiroNome}
                      readOnly
                      tabIndex={-1}
                      className="rdo-input-texto rdo-input-texto--readonly"
                    />
                  </div>
                </div>
              </div>
              <div className="rdo-col">
                <label>Responsável pelo Preenchimento</label>
                <div className="relative-input-wrapper">
                  <input
                    ref={responsavelInputRef}
                    type="text"
                    className="rdo-input-texto rdo-input-texto--editavel"
                    value={data.responsavel_preenchimento || ""}
                    onChange={handleResponsavelChange}
                    placeholder="Digite @ para ver membros do projeto"
                    autoComplete="off"
                    onBlur={() => {
                      setTimeout(() => {
                        if (!clicandoSugestaoRef.current) {
                          setMostrarSugestoesMembros(false);
                        }
                      }, 150);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && mostrarSugestoesMembros && sugestoesMembros.length > 0) {
                        e.preventDefault();
                        inserirResponsavel(sugestoesMembros[0]);
                      }
                      if (e.key === "Escape") {
                        setMostrarSugestoesMembros(false);
                      }
                    }}
                  />
                  {mostrarSugestoesMembros && sugestoesMembros.length > 0 && (
                    <div className="suggestions-dropdown">
                      {sugestoesMembros.map(membro => (
                        <div
                          key={membro.id}
                          onMouseDown={() => { clicandoSugestaoRef.current = true; }}
                          onClick={() => inserirResponsavel(membro)}
                          className="suggestion-item"
                        >
                          <div className="suggestion-avatar-placeholder">
                            {(membro.nickname || membro.nome)?.charAt(0).toUpperCase() || "?"}
                          </div>
                          {membro.nickname || membro.nome}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
            </div>

            {/* Linha separadora única */}
            <hr className="rdo-separador" />

            <div className="rdo-datas-info">
            <div className="rdo-datas-info-checkboxes">
              <label className="rdo-checkbox-inline">
                <input
                  type="checkbox"
                  checked={data.feriado}
                  onChange={(e) => atualizarCampoEspecial("feriado", e.target.checked)}
                />
                Dia de feriado
              </label>
              <label className="rdo-checkbox-inline">
                <input
                  type="checkbox"
                  checked={data.dia_compensado}
                  onChange={(e) => atualizarCampoEspecial("dia_compensado", e.target.checked)}
                />
                Dia compensado em outra data
              </label>
            </div>
            <div className="rdo-datas-info-checkboxes">
              <div className="rdo-datas-info-item">
                <span className="rdo-datas-info-label">Início da Obra: </span>
                <span className="rdo-datas-info-valor">{data.inicio_obra ? formatDate(data.inicio_obra) : "—"}</span>
              </div>
              <FaChevronRight className="rdo-datas-info-seta" />
              <div className="rdo-datas-info-item">
                <span className="rdo-datas-info-label">Término Previsto: </span>
                <span className="rdo-datas-info-valor">{data.termino_obra ? formatDate(data.termino_obra) : "—"}</span>
              </div>
            </div>
          </div>
          </div>

          

          {/* ── AQUI abre o fieldset, envolvendo o resto do formulário ── */}
          <fieldset disabled={edicaoBloqueada} className="rdo-fieldset">

          <div className="rdo-section">
            <h3><FontAwesomeIcon icon={faCloudSun} /> Clima</h3>

            {/* ── Envolve o bloco fixo + tabela em uma linha flex ── */}
            <div className="rdo-clima-linha">
              <div className="rdo-clima-fixo">
                {[
                  { key: "manha", label: "10h" },
                  { key: "tarde", label: "15h" },
                  { key: "noite", label: "19h" },
                ].map(({ key, label }) => {
                  const registro = climaPorPeriodo(key);
                  const iconeInfo = getIconeClima(registro?.icone);
                  return (
                    <div key={key} className="rdo-clima-fixo-item">
                      <span className="rdo-clima-fixo-hora">{label}</span>
                      <div className="rdo-clima-fixo-icone">
                        {iconeInfo ? (
                          <FontAwesomeIcon icon={iconeInfo.icon} style={{ color: iconeInfo.color }} size="lg" />
                        ) : (
                          <span className="rdo-clima-fixo-placeholder">—</span>
                        )}
                      </div>
                      <span className="rdo-clima-fixo-condicao">
                        {registro ? registro.condicao : "Aguardando"}
                      </span>
                      {registro?.temperatura != null && (
                        <span className="rdo-clima-fixo-temp">{Math.round(registro.temperatura)}°C</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {usarNovoLayoutClima ? (
                <div className="rdo-clima-op-novo">
                  <span className="rdo-clima-op-titulo">Obra Operacional</span>
                  <table>
                    <tbody>
                      <tr>
                        <td>Manhã</td>
                        <td>
                          <div className={`rdo-op-text ${data.obra_op_manha === "sim" ? "selected" : ""}`} onClick={() => updateField("obra_op_manha", "sim")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("obra_op_manha", "sim")} aria-label="Sim">Sim</div>
                          <div className={`rdo-op-text ${data.obra_op_manha === "nao" ? "selected" : ""}`} onClick={() => updateField("obra_op_manha", "nao")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("obra_op_manha", "nao")} aria-label="Não">Não</div>
                        </td>
                      </tr>
                      <tr>
                        <td>Tarde</td>
                        <td>
                          <div className={`rdo-op-text ${data.obra_op_tarde === "sim" ? "selected" : ""}`} onClick={() => updateField("obra_op_tarde", "sim")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("obra_op_tarde", "sim")} aria-label="Sim">Sim</div>
                          <div className={`rdo-op-text ${data.obra_op_tarde === "nao" ? "selected" : ""}`} onClick={() => updateField("obra_op_tarde", "nao")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("obra_op_tarde", "nao")} aria-label="Não">Não</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Período</th>
                      <th>Condição</th>
                      <th>Obra Operacional</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Manhã</td>
                      <td>
                        <div className={`rdo-clima-icon ${data.clima_manha === "chuvoso" ? "selected" : ""}`} onClick={() => updateField("clima_manha", "chuvoso")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("clima_manha", "chuvoso")} aria-label="Chuvoso">
                          <FontAwesomeIcon icon={faCloudShowersHeavy} />
                        </div>
                        <div className={`rdo-clima-icon ${data.clima_manha === "seco" ? "selected" : ""}`} onClick={() => updateField("clima_manha", "seco")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("clima_manha", "seco")} aria-label="Ensolarado">
                          <FontAwesomeIcon icon={faSun} />
                        </div>
                      </td>
                      <td>
                        <div className={`rdo-op-text ${data.obra_op_manha === "sim" ? "selected" : ""}`} onClick={() => updateField("obra_op_manha", "sim")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("obra_op_manha", "sim")} aria-label="Sim">Sim</div>
                        <div className={`rdo-op-text ${data.obra_op_manha === "nao" ? "selected" : ""}`} onClick={() => updateField("obra_op_manha", "nao")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("obra_op_manha", "nao")} aria-label="Não">Não</div>
                      </td>
                    </tr>
                    <tr>
                      <td>Tarde</td>
                      <td>
                        <div className={`rdo-clima-icon ${data.clima_tarde === "chuvoso" ? "selected" : ""}`} onClick={() => updateField("clima_tarde", "chuvoso")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("clima_tarde", "chuvoso")} aria-label="Chuvoso">
                          <FontAwesomeIcon icon={faCloudShowersHeavy} />
                        </div>
                        <div className={`rdo-clima-icon ${data.clima_tarde === "seco" ? "selected" : ""}`} onClick={() => updateField("clima_tarde", "seco")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("clima_tarde", "seco")} aria-label="Ensolarado">
                          <FontAwesomeIcon icon={faSun} />
                        </div>
                      </td>
                      <td>
                        <div className={`rdo-op-text ${data.obra_op_tarde === "sim" ? "selected" : ""}`} onClick={() => updateField("obra_op_tarde", "sim")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("obra_op_tarde", "sim")} aria-label="Sim">Sim</div>
                        <div className={`rdo-op-text ${data.obra_op_tarde === "nao" ? "selected" : ""}`} onClick={() => updateField("obra_op_tarde", "nao")} role="button" tabIndex={0} onKeyPress={(e) => e.key === "Enter" && updateField("obra_op_tarde", "nao")} aria-label="Não">Não</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="rdo-section">
            <div className="rdo-section-header">
              <h3><FontAwesomeIcon icon={faPersonDigging} /> Atividades Executadas</h3>
              <div className="rdo-add-button-group">
                <img src={dataTransferImage} alt="Copiar do último RDO" className="data-transfer-icon" onClick={() => copiarDoUltimoRdo("atividades")} width="24" height="24" />
                <button type="button" className="rdo-add-pavimento-btn" onClick={() => setMostrarPavimentosDisponiveis(prev => !prev)} disabled={pavimentosDisponiveis.length === 0} title={pavimentosDisponiveis.length === 0 ? "Todos os pavimentos já estão incluídos" : "Adicionar pavimento"}>+</button>
              </div>
            </div>

            {mostrarPavimentosDisponiveis && pavimentosDisponiveis.length > 0 && (
              <div className="rdo-pavimento-select-dropdown">
                {pavimentosDisponiveis.map((pav) => (
                  <div key={pav} className="rdo-pavimento-select-item" onClick={() => { if (edicaoBloqueada) return; setPavimentosAtividades(prev => [...prev, { pavimento: pav, descricao: "", topicos: [TOPICO_VAZIO()], fotos: [] }]); setMostrarPavimentosDisponiveis(false); }}>
                    {pav}
                  </div>
                ))}
              </div>
            )}

            {pavimentosAtividades.length === 0 ? (
              <p>Nenhum pavimento cadastrado no projeto.</p>
            ) : (
              pavimentosAtividades.map((item, idx) => {
                const podeRemover = !edicaoBloqueada && item.descricao.trim() === "";
                return (
                  <div key={idx} className="rdo-pavimento-linha">
                    <div className="rdo-pavimento-nome">{item.pavimento}</div>
                    <div className="rdo-pavimento-descricao">
                      {usarTopicosAtividades ? (
                        <div className="rdo-topicos">
                          {(item.topicos || [TOPICO_VAZIO()]).map((t, ti) => {
                            const opcoes = t.empresa && !empresasDisponiveis.includes(t.empresa)
                              ? [...empresasDisponiveis, t.empresa]
                              : empresasDisponiveis;
                            return (
                              <div key={ti} className="rdo-topico-linha">
                                <TopicoTextarea
                                  inputRef={(el) => { topicoRefs.current[`${item.pavimento}-${ti}`] = el; }}
                                  value={t.texto}
                                  onChange={(e) => updateTopico(item.pavimento, ti, "texto", e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") { e.preventDefault(); addTopico(item.pavimento, ti); }
                                    if (e.key === "Backspace" && t.texto === "" && (item.topicos || []).length > 1) {
                                      e.preventDefault();
                                      removeTopico(item.pavimento, ti);
                                      setTimeout(() => topicoRefs.current[`${item.pavimento}-${Math.max(ti - 1, 0)}`]?.focus(), 0);
                                    }
                                  }}
                                  placeholder={ti === 0 ? "Descreva o serviço..." : "Novo tópico..."}
                                />

                                <div className="rdo-topico-empresa-wrap">
                                  <select
                                    className={`rdo-topico-empresa${t.empresa ? "" : " rdo-topico-empresa--vazio"}`}
                                    value={t.empresa || ""}
                                    onChange={(e) => updateTopico(item.pavimento, ti, "empresa", e.target.value)}
                                    title="Empresa responsável"
                                  >
                                    <option value="">Empresa</option>
                                    {opcoes.map((emp) => <option key={emp} value={emp}>{emp}</option>)}
                                  </select>

                                  {idx === 0 && ti === 0 && semEmpresasTerceirizadas && !avisoEmpresaFechado && !edicaoBloqueada && (
                                    <div className="rdo-aviso-balao" role="status">
                                      <button
                                        type="button"
                                        className="rdo-aviso-balao-fechar"
                                        onClick={fecharAvisoEmpresa}
                                        aria-label="Fechar aviso"
                                      >
                                        <FaTimes size={10} />
                                      </button>
                                      <strong>Atenção</strong>
                                      <p>
                                        Para as empresas aparecerem aqui, preencha primeiro o campo
                                        <b> Efetivo Terceirizado</b>.
                                      </p>
                                    </div>
                                  )}
                                </div>

                                {!edicaoBloqueada && (
                                  <div className="rdo-topico-acoes">
                                    <button
                                      type="button"
                                      className="rdo-topico-btn rdo-topico-menos"
                                      onClick={() => {
                                        removeTopico(item.pavimento, ti);
                                        setTimeout(() => topicoRefs.current[`${item.pavimento}-${Math.max(ti - 1, 0)}`]?.focus(), 0);
                                      }}
                                      disabled={t.texto.trim() !== "" || (item.topicos || []).length <= 1}
                                      title={t.texto.trim() !== "" ? "Apague o texto para remover a linha" : "Remover linha"}
                                      aria-label="Remover linha"
                                    >
                                      −
                                    </button>
                                    <button
                                      type="button"
                                      className="rdo-topico-btn rdo-topico-mais"
                                      onClick={() => addTopico(item.pavimento, ti)}
                                      title="Adicionar linha abaixo"
                                      aria-label="Adicionar linha abaixo"
                                    >
                                      +
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        (() => {
                          const temTexto = item.descricao.trim() !== "";
                          const editavel = !temTexto || pavimentoEditando === item.pavimento;
                          return editavel ? (
                            <textarea
                              value={item.descricao}
                              onChange={(e) => updatePavimentoDescricao(item.pavimento, e.target.value)}
                              onFocus={() => setPavimentoEditando(item.pavimento)}
                              onBlur={() => { if (item.descricao.trim() !== "") setPavimentoEditando(null); }}
                              placeholder={`Descreva os serviços realizados no ${item.pavimento}...`}
                              autoFocus={pavimentoEditando === item.pavimento}
                            />
                          ) : (
                            <p
                              className="rdo-pavimento-descricao-texto"
                              onClick={() => { if (!edicaoBloqueada) setPavimentoEditando(item.pavimento); }}
                            >
                              {item.descricao}
                            </p>
                          );
                        })()
                      )}
                      {item.fotos?.length > 0 && (
                        <div className="rdo-pavimento-fotos">
                          {item.fotos.map((foto, fi) => {
                            const chave = `${item.pavimento}-${fi}`;
                            const editando = legendaEditando === chave;
                            return (
                              <div key={fi} className="rdo-pavimento-foto-item">
                                <img
                                  src={foto.isNew ? foto.previewUrl : foto.url}
                                  alt={foto.legenda || `Foto ${fi + 1}`}
                                  onClick={() => setFotoPavimentoEmExibicao({ pavimento: item.pavimento, index: fi })}
                                />
                                {!edicaoBloqueada && (
                                  <button
                                    type="button"
                                    className="rdo-pavimento-foto-remover"
                                    onClick={() => removerFotoPavimento(item.pavimento, fi)}
                                    aria-label="Remover foto"
                                  >
                                    <FaTrash size={9} />
                                  </button>
                                )}
                                {editando ? (
                                  <input
                                    type="text"
                                    autoFocus
                                    className="rdo-pavimento-foto-legenda-input"
                                    value={foto.legenda || ""}
                                    onChange={(e) => atualizarLegendaFotoPavimento(item.pavimento, fi, e.target.value)}
                                    onBlur={() => setLegendaEditando(null)}
                                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                    placeholder="Legenda (opcional)"
                                    disabled={edicaoBloqueada}
                                  />
                                ) : (
                                  <span
                                    className="rdo-pavimento-foto-legenda"
                                    onClick={() => { if (!edicaoBloqueada) setLegendaEditando(chave); }}
                                  >
                                    {foto.legenda?.trim() ? foto.legenda : (edicaoBloqueada ? "" : "+ legenda")}
                                  </span>
                                )}
                                <span className="rdo-pavimento-foto-auto">
                                  {formatDate(dataOriginal)} · {item.pavimento}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="rdo-pavimento-acoes">
                      <button
                        type="button"
                        className="rdo-pavimento-historico"
                        onClick={() => abrirHistoricoPavimento(item.pavimento)}
                        title="Histórico do pavimento"
                        aria-label={`Histórico de ${item.pavimento}`}
                      >
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                      </button>
                      <button
                        type="button"
                        className="rdo-pavimento-camera"
                        onClick={() => abrirSeletorFotoPavimento(item.pavimento)}
                        disabled={edicaoBloqueada || (item.fotos?.length || 0) >= limiteFotosPorPavimento}
                        title={(item.fotos?.length || 0) >= limiteFotosPorPavimento ? `Máximo de ${limiteFotosPorPavimento} fotos atingido` : "Adicionar foto"}
                        aria-label={`Adicionar foto ao ${item.pavimento}`}
                      >
                        <FontAwesomeIcon icon={faCamera} />
                      </button>
                      <button
                        type="button"
                        className="rdo-pavimento-remover"
                        onClick={() => removerPavimentoDoRdo(item.pavimento)}
                        disabled={!podeRemover}
                        title={item.descricao.trim() !== "" ? "Esvazie a descrição para remover" : "Remover pavimento"}
                        aria-label={`Remover ${item.pavimento}`}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="rdo-section rdo-efetivos-container">
            <div className="rdo-efetivo-col">
              <div className="rdo-section-header">
                <h3><FontAwesomeIcon icon={faUserGroup} /> Efetivo Próprio</h3>
                <div className="rdo-add-button-group">
                  <img src={dataTransferImage} alt="Copiar do último RDO" className="data-transfer-icon" onClick={() => copiarDoUltimoRdo("efetivo_proprio")} width="24" height="24" />
                  <button type="button" onClick={() => addRow("efetivo_proprio")}>+</button>
                </div>
              </div>
              <table>
                <thead><tr><th>Função</th><th>Total</th><th>Presentes</th><th></th></tr></thead>
                <tbody>
                  {data.efetivo_proprio.map((item, idx) => (
                    <tr key={idx}>
                      <td><input type="text" value={item.funcao || ""} onChange={(e) => updateArrayField("efetivo_proprio", idx, "funcao", e.target.value)} /></td>
                      <td><input type="number" value={item.total || ""} onChange={(e) => updateArrayField("efetivo_proprio", idx, "total", e.target.value)} placeholder="0" /></td>
                      <td><input type="number" value={item.presentes || ""} onChange={(e) => updateArrayField("efetivo_proprio", idx, "presentes", e.target.value)} placeholder="0" /></td>
                      <td><button type="button" className="rdo-pavimento-remover" onClick={() => removeRow("efetivo_proprio", idx)}><FaTrash /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rdo-efetivo-col">
              <div className="rdo-section-header">
                <h3><FontAwesomeIcon icon={faUserGroup} /> Efetivo Terceirizado</h3>
                <div className="rdo-add-button-group">
                  <img src={dataTransferImage} alt="Copiar do último RDO" className="data-transfer-icon" onClick={() => copiarDoUltimoRdo("efetivo_terceirizado")} width="24" height="24" />
                  <button type="button" onClick={() => addRow("efetivo_terceirizado")}>+</button>
                </div>
              </div>
              <table>
                <thead><tr><th>Empresa</th><th>Total</th><th>Presentes</th><th></th></tr></thead>
                <tbody>
                  {data.efetivo_terceirizado.map((item, idx) => (
                    <tr key={idx}>
                      <td><input type="text" value={item.funcao || ""} onChange={(e) => updateArrayField("efetivo_terceirizado", idx, "funcao", e.target.value)} /></td>
                      <td><input type="number" value={item.total || ""} onChange={(e) => updateArrayField("efetivo_terceirizado", idx, "total", e.target.value)} placeholder="0" /></td>
                      <td><input type="number" value={item.presentes || ""} onChange={(e) => updateArrayField("efetivo_terceirizado", idx, "presentes", e.target.value)} placeholder="0" /></td>
                      <td><button type="button" className="rdo-pavimento-remover" onClick={() => removeRow("efetivo_terceirizado", idx)}><FaTrash /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rdo-section">
            <div className="rdo-section-header">
              <h3><FontAwesomeIcon icon={faTruck} /> Recebimento de Materiais</h3>
              <div className="rdo-add-button-group">
                <img src={dataTransferImage} alt="Copiar do último RDO" className="data-transfer-icon" onClick={() => copiarDoUltimoRdo("recebimento_materiais")} width="24" height="24" />
                <button type="button" onClick={() => addRow("recebimento_materiais")}>+</button>
              </div>
            </div>
            <table>
              <thead>
                <tr><th>Código</th><th>Descrição</th><th>Unidade</th><th>Quantidade</th><th>Empresa</th><th></th></tr>
              </thead>
              <tbody>
                {data.recebimento_materiais.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <div className="codigo-com-lupa">
                        <input type="text" value={item.codigo || ""} onChange={(e) => updateArrayField("recebimento_materiais", idx, "codigo", e.target.value)} onBlur={() => buscarMaterialPorCodigo(idx, item.codigo)} onKeyPress={(e) => e.key === "Enter" && buscarMaterialPorCodigo(idx, item.codigo)} placeholder="Cód." />
                        <button type="button" className="lupa-busca-btn" onClick={() => abrirBuscaInsumo(idx, "recebimento_materiais")} title="Buscar material"><FaMagnifyingGlass /></button>
                      </div>
                    </td>
                    <td><input type="text" value={item.descricao || ""} readOnly className="rdo-input-readonly" placeholder="Descrição" /></td>
                    <td><input type="text" value={item.unidade || ""} readOnly className="rdo-input-readonly" placeholder="Un." /></td>
                    <td><input type="number" value={item.quantidade || ""} onChange={(e) => updateArrayField("recebimento_materiais", idx, "quantidade", e.target.value)} placeholder="0" /></td>
                    <td><input type="text" value={item.empresa || ""} onChange={(e) => updateArrayField("recebimento_materiais", idx, "empresa", e.target.value)} placeholder="Empresa" /></td>
                    <td><button type="button" className="rdo-pavimento-remover" onClick={() => removeRow("recebimento_materiais", idx)}><FaTrash /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rdo-section">
            <div className="rdo-section-header">
              <h3><FontAwesomeIcon icon={faGear} /> Equipamentos</h3>
              <div className="rdo-add-button-group">
                <img src={dataTransferImage} alt="Copiar do último RDO" className="data-transfer-icon" onClick={() => copiarDoUltimoRdo("equipamentos")} width="24" height="24" />
                <button type="button" onClick={() => addRow("equipamentos")}>+</button>
              </div>
            </div>
            <table>
              <thead><tr><th>Código</th><th>Descrição</th><th>Total</th><th>Em Uso</th><th></th></tr></thead>
              <tbody>
                {data.equipamentos.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <div className="codigo-com-lupa">
                        <input type="text" value={item.codigo || ""} onChange={(e) => updateArrayField("equipamentos", idx, "codigo", e.target.value)} onBlur={() => buscarItemPorCodigo(idx, item.codigo)} onKeyPress={(e) => e.key === "Enter" && buscarItemPorCodigo(idx, item.codigo)} placeholder="Cód." />
                        <button type="button" className="lupa-busca-btn" onClick={() => abrirBuscaInsumo(idx, "equipamentos")} title="Buscar insumo"><FaMagnifyingGlass /></button>
                      </div>
                    </td>
                    <td><input type="text" value={item.descricao || ""} readOnly className="rdo-input-readonly" placeholder="Descrição" /></td>
                    <td><input type="number" value={item.total || ""} onChange={(e) => updateArrayField("equipamentos", idx, "total", e.target.value)} placeholder="0" /></td>
                    <td><input type="number" value={item.em_uso || ""} onChange={(e) => updateArrayField("equipamentos", idx, "em_uso", e.target.value)} placeholder="0" /></td>
                    <td><button type="button" className="rdo-pavimento-remover" onClick={() => removeRow("equipamentos", idx)}><FaTrash /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rdo-section">
            <div className="rdo-section-header1">
              <label className="rdo-checkbox-label">
                <input
                  type="checkbox"
                  checked={data.tem_intercorrencias}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setData(prev => ({
                      ...prev,
                      tem_intercorrencias: checked,
                      intercorrencias: checked ? prev.intercorrencias : ""
                    }));
                  }}
                />
              </label>
              <h3><FontAwesomeIcon icon={faTriangleExclamation} /> Intercorrências</h3>
            </div>
            {data.tem_intercorrencias && (
              <textarea
                value={data.intercorrencias || ""}
                onChange={(e) => updateField("intercorrencias", e.target.value)}
                placeholder="Descreva imprevistos, paralisações, não conformidades, etc."
              />
            )}
          </div>

          {data.fotos.length > 0 && (
            <div className="rdo-section">
              <h3><FontAwesomeIcon icon={faCamera} /> Fotos</h3>
              <div className="rdo-fotos-preview">
                {data.fotos.map((foto, idx) => (
                  <div key={idx} className="rdo-foto-item" onClick={() => { setFotoEmExibicao(idx); document.body.classList.add("no-scroll"); }}>
                    <img src={typeof foto === "string" ? foto : URL.createObjectURL(foto)} alt={`Foto ${idx + 1}`} className="rdo-foto-thumb" />
                    <button type="button" className="rdo-foto-remove" onClick={(e) => { e.stopPropagation(); setData(prev => ({ ...prev, fotos: prev.fotos.filter((_, i) => i !== idx) })); }} aria-label="Remover foto">
                      <FaTrash size={10} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          </fieldset>

          <div className="rdo-actions">
            <div className="rdo-export-buttons">
              <button
                  type="button"
                  className="rdo-export-btn rdo-print-btn"
                  onClick={() => RdoPdf.imprimir(projetoNome, dataOriginal, montarDadosPdf())}
                  title="Imprimir RDO"
                >
                <FontAwesomeIcon icon={faPrint} /><span>Impressão</span>
              </button>
              <button
                type="button"
                className="rdo-export-btn rdo-pdf-btn"
                onClick={() => RdoPdf.exportar(projetoNome, dataOriginal, montarDadosPdf())}
                title="Exportar para PDF"
              >
                <FontAwesomeIcon icon={faFilePdf} /><span>PDF</span>
              </button>
            </div>
            <div className="rdo-main-actions">
              <button className="btn-cancel" onClick={handleCancel} disabled={loading}>Cancelar</button>
              <button
                className="btn-save"
                onClick={saveRdo}
                disabled={loading || edicaoBloqueada}
                title={edicaoBloqueada ? (bloqueadoFuturo ? "RDO ainda não disponível para edição" : "RDO bloqueado para edição (mais de 7 dias)") : undefined}
              >
                {loading ? "Salvando..." : "Salvar RDO"}
              </button>
            </div>
          </div>
        </div>
      )}

      {fotoEmExibicao !== null && (
        <div className="rdo-lightbox" onClick={() => { setFotoEmExibicao(null); document.body.classList.remove("no-scroll"); }}>
          <div className="rdo-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={typeof data.fotos[fotoEmExibicao] === "string" ? data.fotos[fotoEmExibicao] : URL.createObjectURL(data.fotos[fotoEmExibicao])} alt={`Foto ${fotoEmExibicao + 1}`} className="rdo-lightbox-img" />
            {data.fotos.length > 1 && (
              <>
                <button className="rdo-lightbox-nav rdo-lightbox-prev" onClick={(e) => { e.stopPropagation(); setFotoEmExibicao(prev => prev === 0 ? data.fotos.length - 1 : prev - 1); }}>❮</button>
                <button className="rdo-lightbox-nav rdo-lightbox-next" onClick={(e) => { e.stopPropagation(); setFotoEmExibicao(prev => prev === data.fotos.length - 1 ? 0 : prev + 1); }}>❯</button>
              </>
            )}
            <div className="rdo-lightbox-counter">{fotoEmExibicao + 1} / {data.fotos.length}</div>
          </div>
        </div>
      )}

      {fotoPavimentoEmExibicao !== null && (() => {
        const item = pavimentosAtividades.find(i => i.pavimento === fotoPavimentoEmExibicao.pavimento);
        const foto = item?.fotos?.[fotoPavimentoEmExibicao.index];
        if (!foto) return null;
        return (
          <div className="rdo-lightbox" onClick={() => setFotoPavimentoEmExibicao(null)}>
            <div className="rdo-lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={foto.isNew ? foto.previewUrl : foto.url} alt={foto.legenda || fotoPavimentoEmExibicao.pavimento} className="rdo-lightbox-img" />
              <div className="rdo-lightbox-legenda">
                {foto.legenda && <div>{foto.legenda}</div>}
                <div className="rdo-lightbox-legenda-auto">{formatDate(dataOriginal)} · {fotoPavimentoEmExibicao.pavimento}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {historicoPavimento && (() => {
        const termo = buscaHistorico.trim().toLowerCase();
        const itensFiltrados = termo
          ? historicoPavimento.itens.filter((it) => it.descricao.toLowerCase().includes(termo))
          : historicoPavimento.itens;

        return (
          <div className="rdo-historico-overlay" onClick={() => setHistoricoPavimento(null)}>
            <div className="rdo-historico-content" onClick={(e) => e.stopPropagation()}>
              <div className="rdo-historico-header">
                <span>Histórico · {historicoPavimento.pavimento}</span>
                <button type="button" onClick={() => setHistoricoPavimento(null)} aria-label="Fechar"><FaTimes /></button>
              </div>

              <div className="rdo-historico-busca">
                <FaMagnifyingGlass />
                <input
                  type="text"
                  value={buscaHistorico}
                  onChange={(e) => setBuscaHistorico(e.target.value)}
                  placeholder="Buscar palavra nas descrições..."
                />
                {buscaHistorico && (
                  <button type="button" className="rdo-historico-busca-limpar" onClick={() => setBuscaHistorico("")} aria-label="Limpar busca">
                    <FaTimes size={12} />
                  </button>
                )}
              </div>

              <div className="rdo-historico-timeline">
                {carregandoHistorico ? (
                  <p>Carregando...</p>
                ) : itensFiltrados.length === 0 ? (
                  <p>{termo ? "Nenhum registro encontrado para essa palavra." : "Nenhum registro anterior encontrado para este pavimento."}</p>
                ) : (
                  itensFiltrados.map((it) => (
                    <div key={it.id} className="rdo-historico-timeline-item">
                      <div className="rdo-historico-timeline-data">{formatDate(it.data)}</div>
                      <div className="rdo-historico-timeline-linha">
                        <div className="rdo-historico-timeline-no" />
                      </div>
                      <div className="rdo-historico-timeline-corpo">
                        <div className="rdo-historico-timeline-desc">{destacarTexto(it.descricao, buscaHistorico)}</div>
                        {it.fotos?.length > 0 && (
                          <div className="rdo-historico-item-fotos">
                            {it.fotos.map((f, i) => (
                              <img key={i} src={f.url} alt={f.legenda || ""} title={f.legenda || ""} />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal de confirmação ao sair com edições não salvas ── */}
      {mostrarConfirmSair && (
        <div className="rdo-historico-overlay" onClick={() => setMostrarConfirmSair(false)}>
          <div className="rdo-historico-content" style={{ maxWidth: 420, padding: 24 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Alterações não salvas</h3>
            <p>Você editou este Diário de Obra e ainda não salvou. O que deseja fazer?</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
              <button
                className="btn-save"
                onClick={async () => {
                  setMostrarConfirmSair(false);
                  await saveRdo();
                }}
                disabled={loading}
              >
                {loading ? "Salvando..." : "Salvar e sair"}
              </button>
              <button className="btn-cancel" onClick={() => setMostrarConfirmSair(false)}>
                Continuar editando
              </button>
              <button
                className="rdo-pavimento-remover"
                style={{ width: "100%", padding: "8px 0" }}
                onClick={() => {
                  sessionStorage.removeItem(draftKey);
                  setMostrarConfirmSair(false);
                  onClose && onClose();
                }}
              >
                Descartar alterações
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        type="file"
        accept="image/*"
        multiple
        ref={pavimentoFotoInputRef}
        style={{ display: "none" }}
        onChange={handlePavimentoFotoSelecionada}
      />

      <BuscaInsumo isOpen={buscaInsumoAberta} onClose={() => { setBuscaInsumoAberta(false); setLinhaBuscaAtiva(null); setTipoInsumoAtivo(null); }} onSelect={handleSelecionarInsumo} />
    </div>
  );
};

export default Rdo;